<template>
  <v-container
    class="sipboosts-container"
    fluid
    grid-list-xl
  >
    <v-layout row wrap>
      <v-flex xs12 class="pb-0">
        <material-card 
          color="secondary" 
          :title="isFomoMode ? 'SipBoost Campaign Creative - Sample Data' : 'SipBoost Campaign Creative'" 
          :text="isFomoMode ? 'Digital Boosts can increase your event sales by up to 2.5x per event. Email events@hspbrands.com for more information.' : 'Sample ad sets from your most recent SipBoost campaigns. For more information, email events@hspbrands.com'"
          class="pa-2 mb-0 metrics-card">

          <v-layout class="metrics-row" align-center justify-space-around>

            <v-flex xs12 sm6 md3 lg3 xl3>
              <div class="metric-list-item d-flex align-center justify-space-between">
                <div class="d-flex ">
                  <v-icon small class="mr-2">mdi-eye</v-icon>
                  <div>
                    <div class="metric-legend-title">Impressions</div>
                    <div class="metric-legend-desc">Total times content was shown</div>
                  </div>
                </div>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" small>
                      mdi-information
                    </v-icon>
                  </template>
                  <span>Impressions refer to the amount of times your SipBoost social media invitation was delivered to targeted consumers who reside within a 5-mile geographic radius of the sampling event store.</span>
                </v-tooltip>
              </div>
            </v-flex>

            <v-divider vertical></v-divider>

            <v-flex xs12 sm6 md3 lg3 xl3>
              <div class="metric-list-item d-flex align-center justify-space-between">
                <div class="d-flex ">
                  <v-icon small class="mr-2">mdi-account-group</v-icon>
                  <div>
                    <div class="metric-legend-title">Reach</div>
                    <div class="metric-legend-desc">Unique consumers who saw the social media invitation</div>
                  </div>
                </div>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" small>
                      mdi-information
                    </v-icon>
                  </template>
                  <span>Reach refers to the total number of unique consumers who saw the social media invitation (impressions / reach). On average, reach is ~25% of impressions, which means each person sees the ad about 4 times.</span>
                </v-tooltip>
              </div>
            </v-flex>

            <v-divider vertical></v-divider>

            <v-flex xs12 sm6 md3 lg3 xl3>
              <div class="metric-list-item d-flex align-center justify-space-between">
                <div class="d-flex ">
                  <v-icon small class="mr-2">mdi-cursor-pointer</v-icon>
                  <div>
                    <div class="metric-legend-title">Engagement</div>
                    <div class="metric-legend-desc">People who interacted (click, like, comment, etc.) with the social media invitation</div>
                  </div>
                </div>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" small>
                      mdi-information
                    </v-icon>
                  </template>
                  <span>Engagement is measured through click through rate (clicks / impressions * 100). CTR indicates whether the impressions are resulting in an action - a good CTR benchmark for paid social is 1.2% - 3%</span>
                </v-tooltip>
              </div>
            </v-flex>

          </v-layout>
        </material-card>
      </v-flex>
      <v-flex
        v-for="boost in boosts"
        :key="boost.id"
        xs12
        sm6
        md6
        lg4
      >
        <material-card
          color="tertiary"
          :title="boost.productName"
          :text="isFomoMode ? 'Sample campaign data' : (boost.eventDate ? 'Event date: ' + formatDate(boost.eventDate) : 'Event date pending')"
          class="boost-card"
        >

          <v-card-text v-if="boost.adBody" class="pa-3 ad-body">{{ boost.adBody }}</v-card-text>

          <a :href="boost.imageUrl" target="_blank">
            <v-img
              :src="boost.imageUrl"
              height="750"
              contain
            >
              <template v-slot:placeholder>
                <v-layout
                  class="fill-height ma-0"
                  align="center"
                  justify="center"
                >
                  <v-progress-circular
                    indeterminate
                    color="grey lighten-5"
                  ></v-progress-circular>
                </v-layout>
              </template>
            </v-img>
          </a>

          <v-card-text v-if="boost.event" class="pa-3 event-card">
            <div class="event-date">{{ formatEventDay(boost.event.date) }}<span v-if="boost.event.time_start"> &middot; {{ formatTime(boost.event.time_start) }}<span v-if="boost.event.time_end">&ndash;{{ formatTime(boost.event.time_end) }}</span></span></div>
            <div class="event-name">{{ boost.productName }} tasting at {{ boost.event.store_name || 'the store' }}</div>
            <div v-if="boost.event.address" class="event-address">{{ boost.event.address }}</div>
            <a v-if="boost.eventUrl" :href="boost.eventUrl" target="_blank" rel="noopener" class="event-link">View the event on Facebook</a>
          </v-card-text>

          <v-card-text v-if="boost.hasMetrics" class="pa-3">
            <v-layout row wrap>
              <v-flex xs6 class="pa-2">
                <div class="metric-item">
                  <v-icon small class="mr-1">mdi-eye</v-icon>
                  <span class="metric-label">Impressions</span>
                  <div class="metric-value">{{ Number(boost.impressions).toLocaleString() }}</div>
                </div>
              </v-flex>
              <v-flex xs6 class="pa-2">
                <div class="metric-item">
                  <v-icon small class="mr-1">mdi-account-group</v-icon>
                  <span class="metric-label">Reach</span>
                  <div class="metric-value">{{ Number(boost.reach).toLocaleString() }}</div>
                </div>
              </v-flex>
              <v-flex xs6 class="pa-2">
                <div class="metric-item">
                  <v-icon small class="mr-1">mdi-cursor-pointer</v-icon>
                  <span class="metric-label">CTR</span>
                  <div class="metric-value">{{ boost.ctr }}%</div>
                </div>
              </v-flex>
              <v-flex v-if="boost.link_clicks" xs6 class="pa-2">
                <div class="metric-item">
                  <v-icon small class="mr-1">mdi-link</v-icon>
                  <span class="metric-label">Clicks</span>
                  <div class="metric-value">{{ Number(boost.link_clicks).toLocaleString() }}</div>
                </div>
              </v-flex>
            </v-layout>
          </v-card-text>

          <v-card-text v-else class="pa-3 metrics-pending">
            <v-icon small class="mr-1">mdi-timer-sand</v-icon>
            <span>Performance for this ad is still being confirmed.</span>
          </v-card-text>

        </material-card>
      </v-flex>

      <v-flex v-if="isComingSoon" xs12>
        <v-card class="pa-4 text-center">
          <v-icon size="50" class="mb-3">mdi-clock-time-eight-outline</v-icon>
          <div>Your SipBoost campaign assets are being processed and will appear here soon.</div>
        </v-card>
      </v-flex>

      <v-flex v-if="!boosts.length && !loading && !isComingSoon" xs12>
        <v-card class="pa-4 text-center">
          <v-icon size="50" class="mb-3">mdi-image-multiple-outline</v-icon>
          <div>No boost data available at the moment.</div>
        </v-card>
      </v-flex>

      <v-flex v-if="loading" xs12 class="text-center">
         <v-progress-circular
          indeterminate
          color="primary"
          size="64"
          class="my-10"
        ></v-progress-circular>
        <div>Loading boosts...</div>
      </v-flex>

      <v-flex xs12 class="pb-0">
        <material-card
          color="secondary"
          :title="'SipReviews'"
          :text="'See what consumers are saying about your products online.'"
        >

          <v-flex
            v-for="review in reviews"
            :key="review.id"
            xs12
            sm12
            md12
            lg12
          >

            <material-card
              color="tertiary"
              :title="review.brand"
              class="review-card"
            >
      
              <a :href="review.imageUrl" target="_blank">
                <v-img
                  :src="review.imageUrl"
                  contain
                >
                  <template v-slot:placeholder>
                    <v-layout
                      class="fill-height ma-0"
                      align="center"
                      justify="center"
                    >
                      <v-progress-circular
                        indeterminate
                        color="grey lighten-5"
                      ></v-progress-circular>
                    </v-layout>
                  </template>
                </v-img>
              </a>
            </material-card>
          </v-flex>

          <v-flex v-if="!reviews.length" xs12>
        <v-card class="pa-4 text-center">
          <v-icon size="50" class="mb-3">mdi-image-multiple-outline</v-icon>
          <div>No SipReview images available at the moment.</div>
        </v-card>
      </v-flex>

          </material-card>
      </v-flex>

    </v-layout>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
export default {
  name: 'Boosts',
  data() {
    return {
      loading: true,
      boosts: [],
      reviews: [],
      isFomoMode: false,
      isComingSoon: false
    }
  },
  computed: {

  },
  methods: {
    ...mapActions(['fetchSipBoostImages']),
    ...mapActions(['fetchSipReviews']),

    // Request image URLs and ad metrics
    async loadBoosts() {
      try {
        const response = await this.fetchSipBoostImages()
        if (Array.isArray(response)) {

          // Client has active campaigns but no uploaded screenshots of purchased ad media
          if (response[0] === 'coming soon') {
            this.isComingSoon = true
            this.isFomoMode = false
            this.boosts = []

          //  Client has no active campaigns - show 'em what they're missing.
          //  These carry hasMetrics so the metric grid renders their example
          //  figures; without it every sample reads as pending performance.
          } else if (response[0] === 'fomo') {
            this.isFomoMode = true
            this.isComingSoon = false
            this.boosts = [
              {
                id: 'fomo-1',
                hasMetrics: true,
                productName: 'Antigal Uno Malbec',
                eventDate: '2024-12-15',
                impressions: 11230,
                reach: 9921,
                ctr: 1.8,
                link_clicks: 225,
                imageUrl: response[1]
              },
              {
                id: 'fomo-2',
                hasMetrics: true,
                productName: 'Bib & Tucker Bourbon',
                eventDate: '2024-12-15',
                impressions: 2750,
                reach: 1940,
                ctr: 1.2,
                link_clicks: 22,
                imageUrl: response[2]
              },
              {
                id: 'fomo-3',
                hasMetrics: true,
                productName: 'Bud Light Chelada',
                eventDate: '2024-12-15',
                impressions: 1100,
                reach: 601,
                ctr: 1.4,
                link_clicks: 59,
                imageUrl: response[3]
              }
            ]

          // Client has campaigns with uploaded screenshots of purchased ad media
          } else {
            this.isFomoMode = false
            this.isComingSoon = false
            const boosts = []
            let index = 0
            for (const item of response) {
              if (!item.url) continue
              const metrics = item.metrics
              boosts.push({
                id: metrics ? `m${metrics.id}` : `a${index}`,
                productName: item.product_name || this.brandFromAdSetName(metrics && metrics.ad_set_name),
                eventDate: item.event_date || (metrics && metrics.end_date),
                adBody: item.ad_body || null,
                eventUrl: item.ad_event_url || null,
                event: item.event || null,
                hasMetrics: Boolean(metrics),
                impressions: metrics ? metrics.impressions : null,
                reach: metrics ? metrics.reach : null,
                ctr: metrics ? parseFloat(metrics.ctr) : null,
                link_clicks: metrics ? metrics.clicks : null,
                imageUrl: item.url
              })
              index++
            }
            this.boosts = boosts
          }
        }
      } catch (error) {
        console.error('Error loading boosts:', error)
        this.boosts = []
      } finally {
        this.loading = false
      }
    },
    // Ad set names created from September 2026 begin with the Demo ID, so the
    // first segment is a number rather than the brand. Fall back only when the
    // creative did not carry its own brand.
    formatEventDay(date) {
      if (!date) return ''
      const [y, m, d] = date.split('-').map(Number)
      const dt = new Date(y, m - 1, d)
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${days[dt.getDay()]}, ${months[dt.getMonth()]} ${dt.getDate()}`
    },
    formatTime(t) {
      if (!t) return ''
      const [h, m] = t.split(':').map(Number)
      const suffix = h >= 12 ? 'pm' : 'am'
      const hour = h % 12 === 0 ? 12 : h % 12
      return m ? `${hour}:${String(m).padStart(2, '0')}${suffix}` : `${hour}${suffix}`
    },
    brandFromAdSetName(name) {
      if (!name) return 'SipBoost'
      const parts = name.split('|').map(part => part.trim()).filter(Boolean)
      if (!parts.length) return 'SipBoost'
      return /^\d+$/.test(parts[0]) && parts.length > 1 ? parts[1] : parts[0]
    },
    formatDate(date) {
      if (!date) return null
      var [year, month, day] = date.split('-')
      year = year.slice(2)
      return `${month}/${day}/${year}`
    },
    async loadReviews() {
      try{
        const response = await this.fetchSipReviews()
        console.log('Reviews response:', response)
        const reviews = []
        for (const item of response) {
          reviews.push({
            id: item.id,
            brand: item.producer_name,
            imageUrl: item.url
          })
        }
        this.reviews = reviews
      } catch (error) {
        this.reviews = []
      }
    },
  },
  created() {
    this.loadBoosts()
    this.loadReviews()
  },
}
</script>

<style scoped>
.mb-2 {
  margin-bottom: 0.5rem;
}

.mb-1 {
  margin-bottom: 0.25rem;
}

.sipboosts-container {
  padding-top: 0;
  padding-bottom: 0;
  margin: 0;
}

/* SIPBOOST CARD GALLERY */
.boost-card {
  display: flex;
  flex-direction: column;
}

.boost-card >>> .v-card--material__header {
  padding: 10px 16px !important;
}

.review-card >>> .v-card--material__header {
  padding: 10px 100px !important;
}

.boost-card >>> h4.title, 
.review-card >>> h4.title {
  font-size: 1.2rem !important;
  margin-bottom: 4px !important;
}

.boost-card >>> p.category, 
.review-card >>> p.category {
  font-size: 1.1rem !important;
}

.boost-card .v-card {
  display: flex;
  flex-direction: column;
}

.boost-card .v-card__text {
  flex-grow: 1;
  overflow-y: auto;
}

.boost-card>>>.category {
  color: #FFFFFF !important;
}

.review-card {
  display: flex;
  flex-direction: column;
}

/* METRICS LEGEND */
.metrics-card {
  padding-top: 0px;
  padding-bottom: 0px;
  margin-top: 15px !important;
}

.metrics-card >>> p {
  color: #FFFF !important;
}


.metrics-row {
  margin-left: 0;
  margin-right: 0;
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
}

.metric-list-item {
  display: flex;
  align-items: center;
  padding: 8px;
}

.metric-list-item+.metric-list-item {
  border-left: 1px solid rgba(0, 0, 0, 0.12);
}

.metric-legend-title {
  font-weight: 600;
  font-size: 16px;
  line-height: 1.1;
}

.metric-legend-desc {
  font-size: 14px;
  color: #000D;
  font-weight: 300;
  line-height: 1.1;
}

@media (max-width: 750px) {
  .metrics-row {
    flex-wrap: wrap !important
  }
}

.metrics-pending {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.875rem;
  display: flex;
  align-items: center;
}

.ad-body {
  white-space: pre-line;
  font-size: 0.9rem;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.82);
}
.event-card {
  background: #f5f6f8;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.event-date {
  color: #c0392b;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.event-name {
  font-weight: 600;
  margin-top: 2px;
}
.event-address {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.85rem;
}
.event-link {
  display: inline-block;
  margin-top: 6px;
  font-size: 0.85rem;
}
</style>